import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import * as XLSX from 'xlsx';
import { keyframes } from '@emotion/react';
import {
    Badge,
    Box,
    Button,
    Circle,
    Divider,
    Flex,
    Heading,
    Icon,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useToast,
} from '@chakra-ui/react';
import { HiOutlineInformationCircle, HiSparkles } from 'react-icons/hi2';

import PageHeader from 'layout/pageHeader';
import MDropZone from 'components/molecules/MDropZone';
import { EXCEL_FILE_TYPE } from 'shared/const';
import { TGeneratedTitleSplitRow } from 'core/API/titleSplitAiApi';
import keys from 'config';
import icons from 'shared/icons';

const ROWS_COMMIT_INTERVAL_MS = 250;
const SKELETON_ROWS = [0, 1, 2, 3, 4, 5];
const AI_GRADIENT = 'linear-gradient(120deg, #0267EB 0%, #6C4BF6 45%, #12B5CB 100%)';
const TABULAR_NUMS = { fontVariantNumeric: 'tabular-nums' };

const fadeUp = keyframes`
    from { opacity: 0; transform: translateY(6px); }
    to { opacity: 1; transform: translateY(0); }
`;
const shimmer = keyframes`
    0% { background-position: -300px 0; }
    100% { background-position: 300px 0; }
`;
const sweep = keyframes`
    0% { transform: translateX(-60%); }
    100% { transform: translateX(260%); }
`;
const breathe = keyframes`
    0%, 100% { transform: scale(1); opacity: 0.9; }
    50% { transform: scale(1.08); opacity: 1; }
`;
const halo = keyframes`
    0% { transform: scale(0.9); opacity: 0.55; }
    70%, 100% { transform: scale(1.6); opacity: 0; }
`;
const bounceDot = keyframes`
    0%, 80%, 100% { opacity: 0.25; transform: translateY(0); }
    40% { opacity: 1; transform: translateY(-3px); }
`;
const gradientShift = keyframes`
    0%, 100% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
`;

const formatDuration = (ms: number) => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return minutes ? `${minutes}m ${String(seconds).padStart(2, '0')}s` : `${seconds}s`;
};

const getConfidenceScheme = (confidence: number) => {
    if (confidence >= 85) {
        return 'green';
    }
    if (confidence >= 60) {
        return 'orange';
    }
    return 'red';
};

const ThinkingDots = () => (
    <Flex as="span" gap="3px" alignItems="center">
        {[0, 1, 2].map((index) => (
            <Box
                key={index}
                as="span"
                w="4px"
                h="4px"
                borderRadius="full"
                bg="currentColor"
                animation={`${bounceDot} 1.2s ease-in-out ${index * 0.15}s infinite`}
            />
        ))}
    </Flex>
);

const TitleSplitAIGenerate = () => {
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const language = 'English';
    const [generatedRows, setGeneratedRows] = useState<Record<string, any>[]>([]);
    const [isGenerating, setIsGenerating] = useState(false);
    const [streamCompleted, setStreamCompleted] = useState(false);
    const [generationStopped, setGenerationStopped] = useState(false);
    const [elapsedMs, setElapsedMs] = useState(0);
    const [leftPanelHeight, setLeftPanelHeight] = useState<number>(0);
    const abortControllerRef = useRef<AbortController | null>(null);
    const leftPanelRef = useRef<HTMLDivElement | null>(null);
    const pendingRowsRef = useRef<TGeneratedTitleSplitRow[]>([]);
    const commitTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const elapsedTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const navigate = useNavigate();
    const toast = useToast();
    const UploadIcon = icons.upload;
    const DownloadIcon = icons.download;
    const CheckIcon = icons.check;
    const ClearIcon = icons.clear;

    const apiBase = useMemo(() => String(keys.API_BASE || '').replace(/\/$/, ''), []);
    const apiRoot = useMemo(() => (apiBase.endsWith('/api') ? apiBase : `${apiBase}/api`), [apiBase]);

    useEffect(() => {
        if (!leftPanelRef.current) {
            return;
        }

        const updateHeight = () => {
            if (leftPanelRef.current) {
                setLeftPanelHeight(Math.ceil(leftPanelRef.current.getBoundingClientRect().height));
            }
        };

        updateHeight();

        const observer = new ResizeObserver(() => {
            updateHeight();
        });

        observer.observe(leftPanelRef.current);
        window.addEventListener('resize', updateHeight);

        return () => {
            observer.disconnect();
            window.removeEventListener('resize', updateHeight);
        };
    }, []);

    const resultColumns = useMemo(() => {
        const columns = new Set<string>();
        generatedRows.forEach((row) =>
            Object.keys(row)
                .filter((key) => key !== 'confidence')
                .forEach((key) => columns.add(key)),
        );
        return Array.from(columns);
    }, [generatedRows]);

    const averageConfidence = useMemo(() => {
        const scored = generatedRows.filter((row) => typeof row.confidence === 'number');
        if (!scored.length) {
            return null;
        }
        return Math.round(scored.reduce((sum, row) => sum + Number(row.confidence), 0) / scored.length);
    }, [generatedRows]);

    const handleChangeFile = (file: File | null) => {
        if (file?.type === EXCEL_FILE_TYPE) {
            setUploadedFile(file);
        } else {
            if (file !== null) {
                toast({
                    title: 'Please upload a valid .xlsx file',
                    status: 'error',
                    isClosable: true,
                });
            }
            setUploadedFile(null);
        }
    };

    const goBack = () => navigate(-1);

    const downloadPreviewAsExcel = () => {
        if (!generatedRows.length) {
            return;
        }

        const exportRows = generatedRows.map((row) => {
            const { confidence, ...visibleData } = row;
            return visibleData;
        });

        const worksheet = XLSX.utils.json_to_sheet(exportRows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'AI Results');
        XLSX.writeFile(workbook, 'title_split_ai_results.xlsx');
    };

    const parseSseBlock = (block: string) => {
        const lines = block
            .split('\n')
            .map((line) => line.trim())
            .filter(Boolean);
        let eventName = 'message';
        const dataLines: string[] = [];

        lines.forEach((line) => {
            if (line.startsWith('event:')) {
                eventName = line.slice(6).trim();
            }

            if (line.startsWith('data:')) {
                dataLines.push(line.slice(5).trim());
            }
        });

        return { eventName, data: dataLines.join('\n') };
    };

    const commitPendingRows = () => {
        if (!pendingRowsRef.current.length) {
            return;
        }

        const rows = pendingRowsRef.current;
        pendingRowsRef.current = [];
        setGeneratedRows((prev) => [...prev, ...rows]);
    };

    const stopCommitTimer = () => {
        if (commitTimerRef.current) {
            clearInterval(commitTimerRef.current);
            commitTimerRef.current = null;
        }
    };

    const stopElapsedTimer = () => {
        if (elapsedTimerRef.current) {
            clearInterval(elapsedTimerRef.current);
            elapsedTimerRef.current = null;
        }
    };

    useEffect(
        () => () => {
            stopCommitTimer();
            stopElapsedTimer();
            abortControllerRef.current?.abort();
        },
        [],
    );

    const handleStop = () => abortControllerRef.current?.abort();

    const handleGenerate = async () => {
        if (!uploadedFile) {
            return;
        }

        try {
            abortControllerRef.current?.abort();
            const abortController = new AbortController();
            abortControllerRef.current = abortController;

            pendingRowsRef.current = [];
            setGeneratedRows([]);
            setStreamCompleted(false);
            setGenerationStopped(false);
            setIsGenerating(true);
            setElapsedMs(0);

            stopCommitTimer();
            commitTimerRef.current = setInterval(commitPendingRows, ROWS_COMMIT_INTERVAL_MS);

            stopElapsedTimer();
            const startedAt = Date.now();
            elapsedTimerRef.current = setInterval(() => setElapsedMs(Date.now() - startedAt), 200);

            const formData = new FormData();
            formData.append('excelFile', uploadedFile);
            formData.append('language', language);
            formData.append('profile_type', 'strict');

            const response = await fetch(`${apiRoot}/ai/title-split/generate`, {
                method: 'POST',
                credentials: 'include',
                body: formData,
                signal: abortController.signal,
            });

            if (!response.ok || !response.body) {
                throw new Error('AI generation failed');
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            while (true) {
                const { value, done } = await reader.read();
                if (done) {
                    break;
                }

                buffer += decoder.decode(value, { stream: true });
                const blocks = buffer.split('\n\n');
                buffer = blocks.pop() || '';

                blocks.forEach((block) => {
                    const { eventName, data } = parseSseBlock(block);

                    if (eventName === 'batch') {
                        const rows = JSON.parse(data) as TGeneratedTitleSplitRow[];
                        if (Array.isArray(rows) && rows.length) {
                            pendingRowsRef.current.push(...rows);
                        }
                    }

                    if (eventName === 'complete') {
                        setStreamCompleted(true);
                    }

                    if (eventName === 'error') {
                        const errorPayload = JSON.parse(data || '{}') as { message?: string };
                        throw new Error(errorPayload.message || 'AI generation failed');
                    }
                });
            }

            setStreamCompleted(true);

            toast({
                title: 'AI generation complete',
                status: 'success',
                isClosable: true,
            });
        } catch (error) {
            if ((error as Error).name === 'AbortError') {
                setGenerationStopped(true);
                return;
            }

            toast({
                title: (error as Error).message,
                status: 'error',
                isClosable: true,
            });
        } finally {
            stopCommitTimer();
            stopElapsedTimer();
            commitPendingRows();
            setIsGenerating(false);
        }
    };

    const showSkeleton = isGenerating && generatedRows.length === 0;
    const showEmptyState = !isGenerating && !generatedRows.length;

    return (
        <Box minH="100%" bgGradient="linear(to-b, #F7F8FC, #FFFFFF)">
            <PageHeader
                icon={{ name: 'arrowBack', props: { onClick: goBack } }}
                pageTitle="AI title split generation"
            />
            <Box px="32px" pb="32px" pt={4}>
                <Flex gap={8} direction={{ base: 'column', xl: 'row' }} alignItems="flex-start">
                    <Flex
                        ref={leftPanelRef}
                        w={{ base: '100%', xl: '430px' }}
                        direction="column"
                        position="relative"
                        overflow="hidden"
                        bg="white"
                        borderWidth="1px"
                        borderColor="gray.100"
                        borderRadius="20px"
                        boxShadow="0 12px 36px -22px rgba(15, 23, 42, 0.45)"
                        p={6}
                    >
                        <Box
                            position="absolute"
                            top={0}
                            left={0}
                            right={0}
                            h="3px"
                            bgGradient="linear(to-r, #0267EB, #6C4BF6, #12B5CB)"
                        />

                        <Flex alignItems="center" gap={3} mb={4}>
                            <Circle
                                size="40px"
                                color="white"
                                background={AI_GRADIENT}
                                backgroundSize="200% 200%"
                                animation={`${gradientShift} 6s ease infinite`}
                            >
                                <Icon as={HiSparkles} boxSize={5} />
                            </Circle>
                            <Box>
                                <Heading as="h2" fontWeight="semibold" fontSize="18px" lineHeight="1.2">
                                    Upload a product file
                                </Heading>
                                <Text fontSize="13px" color="gray.500">
                                    AI-assisted title splitting
                                </Text>
                            </Box>
                        </Flex>

                        <Text mb={6} fontSize="14px" color="gray.600">
                            Upload a file containing a list of products to generate AI title split suggestions.
                        </Text>

                        <Flex
                            role="note"
                            alignItems="flex-start"
                            gap={3}
                            mb={6}
                            p={3.5}
                            bg="blue.50"
                            borderWidth="1px"
                            borderColor="blue.100"
                            borderRadius="12px"
                        >
                            <Icon
                                as={HiOutlineInformationCircle}
                                boxSize={5}
                                mt="1px"
                                color="blue.600"
                                flexShrink={0}
                            />
                            <Box>
                                <Text mb={0.5} fontSize="13px" fontWeight="semibold" color="blue.800">
                                    AI-generated suggestions
                                </Text>
                                <Text fontSize="12px" lineHeight="1.5" color="blue.700">
                                    Results may be inaccurate or incomplete. Review each split for accuracy before
                                    using it in your product data.
                                </Text>
                            </Box>
                        </Flex>

                        <MDropZone chosenFile={uploadedFile} handleChangeFile={handleChangeFile} />

                        <Button
                            mt={6}
                            h="46px"
                            color="white"
                            background={AI_GRADIENT}
                            backgroundSize="200% 200%"
                            animation={isGenerating ? `${gradientShift} 3s ease infinite` : undefined}
                            onClick={handleGenerate}
                            isLoading={isGenerating}
                            loadingText="Generating"
                            isDisabled={!uploadedFile || isGenerating}
                            rightIcon={<Icon as={UploadIcon} />}
                            borderRadius="12px"
                            transition="transform 0.2s ease, box-shadow 0.2s ease"
                            _hover={{
                                transform: 'translateY(-1px)',
                                boxShadow: '0 14px 26px -16px rgba(2, 103, 235, 0.95)',
                                background: AI_GRADIENT,
                            }}
                            _active={{ transform: 'translateY(0)', background: AI_GRADIENT }}
                            _disabled={{ opacity: 0.55, cursor: 'not-allowed', background: AI_GRADIENT }}
                        >
                            Generate AI result
                        </Button>

                        {isGenerating ? (
                            <Button
                                mt={3}
                                size="sm"
                                variant="ghost"
                                colorScheme="gray"
                                leftIcon={<Icon as={ClearIcon} />}
                                onClick={handleStop}
                            >
                                Stop generation
                            </Button>
                        ) : null}
                    </Flex>

                    <Box
                        flex={1}
                        w="full"
                        position="relative"
                        overflow="hidden"
                        display="flex"
                        flexDirection="column"
                        borderWidth="1px"
                        borderColor="gray.100"
                        borderRadius="20px"
                        bg="white"
                        boxShadow="0 12px 36px -22px rgba(15, 23, 42, 0.45)"
                        maxH={{ base: 'none', xl: leftPanelHeight ? `${leftPanelHeight}px` : 'none' }}
                    >
                        {isGenerating ? (
                            <Box position="absolute" top={0} left={0} right={0} h="3px" bg="gray.100" overflow="hidden">
                                <Box
                                    h="100%"
                                    w="35%"
                                    background={AI_GRADIENT}
                                    animation={`${sweep} 1.4s cubic-bezier(0.4, 0, 0.2, 1) infinite`}
                                />
                            </Box>
                        ) : null}

                        <Flex
                            justifyContent="space-between"
                            alignItems="center"
                            gap={4}
                            px={5}
                            py={4}
                            flexWrap="wrap"
                            flexShrink={0}
                        >
                            <Flex alignItems="center" gap={3}>
                                <Text fontWeight="semibold">Result preview</Text>
                                {generatedRows.length ? (
                                    <Badge borderRadius="full" px={2.5} py="2px" colorScheme="blue" sx={TABULAR_NUMS}>
                                        {generatedRows.length} rows
                                    </Badge>
                                ) : null}
                                {isGenerating ? (
                                    <Flex alignItems="center" gap={2} color="blueMain" fontSize="12px">
                                        <ThinkingDots />
                                        <Text>streaming</Text>
                                    </Flex>
                                ) : null}
                                {!isGenerating && streamCompleted ? (
                                    <Flex
                                        alignItems="center"
                                        gap={1}
                                        color="green.500"
                                        fontSize="12px"
                                        animation={`${fadeUp} 0.35s ease both`}
                                    >
                                        <Icon as={CheckIcon} boxSize={4} />
                                        <Text>Complete in {formatDuration(elapsedMs)}</Text>
                                    </Flex>
                                ) : null}
                                {!isGenerating && generationStopped ? (
                                    <Flex alignItems="center" gap={1} color="orange.500" fontSize="12px">
                                        <Text>Stopped · partial results</Text>
                                    </Flex>
                                ) : null}
                            </Flex>
                            <Button
                                size="sm"
                                onClick={downloadPreviewAsExcel}
                                leftIcon={<Icon as={DownloadIcon} />}
                                isDisabled={!generatedRows.length}
                                colorScheme="blue"
                                variant="outline"
                                borderRadius="10px"
                            >
                                Download preview (.xlsx)
                            </Button>
                        </Flex>

                        {generatedRows.length || isGenerating ? (
                            <>
                                <Flex px={5} pb={3} gap={8} flexShrink={0} flexWrap="wrap">
                                    <Box>
                                        <Text fontSize="11px" color="gray.400" textTransform="uppercase">
                                            Elapsed
                                        </Text>
                                        <Text fontSize="14px" fontWeight="semibold" sx={TABULAR_NUMS}>
                                            {formatDuration(elapsedMs)}
                                        </Text>
                                    </Box>
                                    <Box>
                                        <Text fontSize="11px" color="gray.400" textTransform="uppercase">
                                            Avg. confidence
                                        </Text>
                                        <Text fontSize="14px" fontWeight="semibold" sx={TABULAR_NUMS}>
                                            {averageConfidence !== null ? `${averageConfidence}%` : '—'}
                                        </Text>
                                    </Box>
                                </Flex>
                                <Divider borderColor="gray.100" />
                            </>
                        ) : null}

                        <Box flex={1} overflow="auto" px={5} pb={4}>
                            {showSkeleton ? (
                                <Box animation={`${fadeUp} 0.3s ease both`}>
                                    <Flex alignItems="center" gap={3} mb={5}>
                                        <Box position="relative">
                                            <Circle
                                                size="34px"
                                                color="white"
                                                background={AI_GRADIENT}
                                                backgroundSize="200% 200%"
                                                animation={`${breathe} 2s ease-in-out infinite, ${gradientShift} 5s ease infinite`}
                                            >
                                                <Icon as={HiSparkles} boxSize={4} />
                                            </Circle>
                                            <Circle
                                                size="34px"
                                                position="absolute"
                                                inset={0}
                                                border="2px solid"
                                                borderColor="blueMain"
                                                animation={`${halo} 2s ease-out infinite`}
                                            />
                                        </Box>
                                        <Box>
                                            <Flex alignItems="center" gap={2} color="gray.700">
                                                <Text fontSize="14px" fontWeight="semibold">
                                                    Analyzing your products
                                                </Text>
                                                <ThinkingDots />
                                            </Flex>
                                            <Text fontSize="12px" color="gray.500">
                                                Detecting the category schema, then splitting every title.
                                            </Text>
                                        </Box>
                                    </Flex>

                                    {SKELETON_ROWS.map((row) => (
                                        <Flex key={row} gap={3} mb={3} opacity={1 - row * 0.13}>
                                            {[3, 6, 4, 2].map((flexValue, cellIndex) => (
                                                <Box
                                                    key={cellIndex}
                                                    flex={flexValue}
                                                    h="12px"
                                                    borderRadius="full"
                                                    bgGradient="linear(to-r, gray.100, gray.200, gray.100)"
                                                    backgroundSize="600px 100%"
                                                    animation={`${shimmer} 1.4s linear ${row * 0.08}s infinite`}
                                                />
                                            ))}
                                        </Flex>
                                    ))}
                                </Box>
                            ) : null}

                            {generatedRows.length ? (
                                <Table size="sm">
                                    <Thead position="sticky" top={0} zIndex={1} bg="white">
                                        <Tr>
                                            {resultColumns.map((column) => (
                                                <Th
                                                    key={column}
                                                    color="gray.500"
                                                    fontSize="11px"
                                                    borderColor="gray.100"
                                                >
                                                    {column}
                                                </Th>
                                            ))}
                                            <Th color="gray.500" fontSize="11px" borderColor="gray.100" isNumeric>
                                                confidence
                                            </Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {generatedRows.map((row, rowIndex) => (
                                            <Tr
                                                key={`${row.ean || rowIndex}-${rowIndex}`}
                                                animation={`${fadeUp} 0.35s ease both`}
                                                transition="background 0.15s ease"
                                                _hover={{ bg: 'gray.50' }}
                                            >
                                                {resultColumns.map((column) => (
                                                    <Td
                                                        key={`${rowIndex}-${column}`}
                                                        borderColor="gray.100"
                                                        fontSize="13px"
                                                    >
                                                        {String(row[column] ?? '')}
                                                    </Td>
                                                ))}
                                                <Td borderColor="gray.100" isNumeric>
                                                    {typeof row.confidence === 'number' ? (
                                                        <Badge
                                                            borderRadius="full"
                                                            px={2}
                                                            sx={TABULAR_NUMS}
                                                            colorScheme={getConfidenceScheme(row.confidence)}
                                                        >
                                                            {row.confidence}%
                                                        </Badge>
                                                    ) : (
                                                        '—'
                                                    )}
                                                </Td>
                                            </Tr>
                                        ))}
                                    </Tbody>
                                </Table>
                            ) : null}

                            {isGenerating && generatedRows.length ? (
                                <Flex mt={4} alignItems="center" gap={2} color="gray.500">
                                    <ThinkingDots />
                                    <Text fontSize="12px">Receiving more rows</Text>
                                </Flex>
                            ) : null}

                            {showEmptyState ? (
                                <Flex
                                    direction="column"
                                    alignItems="center"
                                    justifyContent="center"
                                    textAlign="center"
                                    py={16}
                                    animation={`${fadeUp} 0.4s ease both`}
                                >
                                    <Box position="relative" mb={5}>
                                        <Circle
                                            size="64px"
                                            color="white"
                                            background={AI_GRADIENT}
                                            backgroundSize="200% 200%"
                                            animation={`${breathe} 3.5s ease-in-out infinite, ${gradientShift} 8s ease infinite`}
                                        >
                                            <Icon as={HiSparkles} boxSize={7} />
                                        </Circle>
                                        <Circle
                                            size="64px"
                                            position="absolute"
                                            inset={0}
                                            border="2px solid"
                                            borderColor="blue.200"
                                            animation={`${halo} 3.5s ease-out infinite`}
                                        />
                                    </Box>
                                    <Text fontWeight="semibold" color="gray.700">
                                        No results yet
                                    </Text>
                                    <Text fontSize="14px" color="gray.500" maxW="420px" mt={1}>
                                        Upload a product file and run the generation to watch AI title splits stream in
                                        live.
                                    </Text>
                                </Flex>
                            ) : null}
                        </Box>
                    </Box>
                </Flex>
            </Box>
        </Box>
    );
};

export default TitleSplitAIGenerate;
